// src/components/about/ProfessionalHero.styles.ts
import styled from 'styled-components';
import { motion } from 'framer-motion';

import Button from '../Button';
import { device, minDevice } from '../../styles/breakpoints';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

export const HeroRoot = styled.section`
  padding: ${({ theme }) => theme.spacing['2xl']} 0 ${({ theme }) => theme.spacing['3xl']};
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};

  @media ${minDevice.laptop} {
    /* Matches ClientCta's band. The opening and closing sections get more air
       than the four content sections between them, which sit at spacing['3xl']. */
    padding: ${({ theme }) => theme.spacing['3xl']} 0 6rem;
  }
`;

export const HeroContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing['2xl']};
  align-items: start;

  @media ${minDevice.laptop} {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    gap: 4rem;
  }
`;

/* ---------- Identity column ---------- */

export const HeroMain = styled(motion.header)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  min-width: 0;
`;

export const TitleBlock = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
`;

export const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0;
  font-family: ${sansStack};
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const EyebrowMark = styled.span`
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  background: ${({ theme }) => theme.accent};
`;

export const Name = styled.h1`
  margin: 0;
  font-family: ${sansStack};
  font-size: 2.9rem;
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: ${({ theme }) => theme.text};

  @media ${device.tablet} {
    font-size: 2.4rem;
  }

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

export const Role = styled.p`
  margin: 0;
  max-width: 34ch;
  font-family: ${sansStack};
  font-size: 1.15rem;
  line-height: 1.5;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};

  @media ${device.mobile} {
    font-size: 1.02rem;
  }
`;

export const Supporting = styled(motion.p)`
  margin: 0;
  max-width: 58ch;
  font-family: ${sansStack};
  font-size: 1.02rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ---------- Experience indicator ---------- */

export const ExperienceNote = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 58ch;
  padding: 0.1rem 0 0.1rem 1.15rem;
  border-left: 2px solid ${({ theme }) => theme.accent};
`;

export const ExperienceValue = styled.span`
  font-family: ${sansStack};
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.text};
`;

export const ExperienceLabel = styled.span`
  font-family: ${sansStack};
  font-size: 0.95rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ---------- Closing statement + CTAs ---------- */

export const ClosingBlock = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export const Principle = styled.p`
  position: relative;
  margin: 0;
  max-width: 46ch;
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  font-family: ${sansStack};
  font-size: 1.05rem;
  line-height: 1.6;
  font-weight: 500;
  color: ${({ theme }) => theme.text};

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 48px;
    height: 2px;
    background: ${({ theme }) => theme.accent};
  }
`;

export const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.9rem;
`;

export const PrimaryCta = styled(Button)`
  button {
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.background};
    border-color: ${({ theme }) => theme.accent};
    font-weight: 600;
  }

  button:hover {
    background: ${({ theme }) => theme.text};
    color: ${({ theme }) => theme.background};
    border-color: ${({ theme }) => theme.text};
  }
`;

/* ---------- Focus panel ---------- */

export const FocusPanel = styled(motion.aside)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

export const PanelLabel = styled.p`
  margin: 0;
  font-family: ${monoStack};
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const FocusList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const FocusItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem 0;
  border-top: 1px solid ${({ theme }) => theme.borderColor};

  &:first-child {
    border-top: none;
    padding-top: 0.25rem;
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

export const FocusLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 0.55rem;
  font-family: ${sansStack};
  font-size: 0.98rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

export const FocusMarker = styled.span`
  font-family: ${monoStack};
  font-size: 0.78rem;
  line-height: 1;
  color: ${({ theme }) => theme.accent};
`;

export const FocusDetail = styled.span`
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.secondary};
`;
